import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFidesChat } from '../../context/FidesChatContext';
import { useChatAiAssist } from './useChatAiAssist';
import useChatLinkCorpus from './useChatLinkCorpus';
import useChatViewingPresence from './useChatViewingPresence';
import useFidesChatConversationMeta from './useFidesChatConversationMeta';
import useFidesChatInbox, { FIDES_CHAT_REFRESH_EVENT } from './useFidesChatInbox';
import useFidesChatMessages from './useFidesChatMessages';
import useFidesChatPanelTeam from './useFidesChatPanelTeam';
import useFidesChatTranscript from './useFidesChatTranscript';
import type { ChatResolveEntityOption } from '../../models/admin/AdminChatResolveDraft';
import FidesChatAdminService, { ChatMessage } from '../../services/admin/FidesChatAdminService';
import { buildContextDeepLink } from '../../utils/chatDeepLinks';
import {
  compareChatTimelineItems,
  getChatTimestampMs,
  resolveParticipantAddedEventSortMs,
} from '../../utils/chatTimeline';
import type { ChatTimelineItem } from '../../components/admin/FidesChatMessageTimeline';
import { buildContextLabel, type ChatContext } from '../../components/admin/fidesChatPanelUtils';

export function useFidesChatPanel() {
  const chat = useFidesChat();
  const { user } = useAuth0();
  const { conversations } = useFidesChatInbox(Boolean(chat?.isPanelOpen));
  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => conversation.conversationId === chat?.openConversationId,
      ) ?? null,
    [chat?.openConversationId, conversations],
  );
  const isTranscriptMode = chat?.panelMode === 'transcript';
  const panelEnabled = Boolean(chat?.isPanelOpen);
  const openConversationId = chat?.isPanelOpen ? chat.openConversationId : null;
  const liveChat = useFidesChatMessages(
    !isTranscriptMode ? openConversationId : null,
    panelEnabled && !isTranscriptMode,
  );
  useChatViewingPresence(
    openConversationId,
    panelEnabled && !isTranscriptMode && !chat?.isChatMinimized,
  );
  const transcriptChat = useFidesChatTranscript(
    isTranscriptMode ? openConversationId : null,
    panelEnabled && isTranscriptMode,
  );
  const messages = isTranscriptMode ? transcriptChat.messages : liveChat.messages;
  const chatEvents = isTranscriptMode ? transcriptChat.events : liveChat.events;
  const loading = isTranscriptMode ? transcriptChat.loading : liveChat.loading;
  const error = isTranscriptMode ? transcriptChat.error : liveChat.error;
  const transcriptData = transcriptChat.data;
  const contactExternalIdForCorpus =
    selectedConversation?.contact?.externalId ?? transcriptData?.contactExternalId ?? '';
  const conversationIdForCorpus =
    selectedConversation?.conversationId ?? chat?.openConversationId ?? '';
  const { corpus: chatLinkCorpus } = useChatLinkCorpus(
    chat?.isPanelOpen && contactExternalIdForCorpus ? contactExternalIdForCorpus : null,
    chat?.isPanelOpen && conversationIdForCorpus ? conversationIdForCorpus : null,
  );
  const [optimisticMessages, setOptimisticMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [resolveDraftsOpen, setResolveDraftsOpen] = useState(false);
  const { status: liveConversationStatus, resolvedBy: liveResolvedBy } =
    useFidesChatConversationMeta(chat?.openConversationId ?? null, Boolean(chat?.isPanelOpen));
  const [toastMessage, setToastMessage] = useState<{
    type: 'error' | 'success';
    text: string;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const team = useFidesChatPanelTeam({
    openConversationId: chat?.openConversationId ?? null,
    isPanelOpen: Boolean(chat?.isPanelOpen),
    isTranscriptMode,
    selectedOwnerAdminExternalId: selectedConversation?.ownerAdminExternalId,
  });

  const conversationIdForRestore = chat?.openConversationId;
  const restorePanel = chat?.restorePanel;

  useEffect(() => {
    setOptimisticMessages([]);
    setInputValue('');
    restorePanel?.();
  }, [conversationIdForRestore, restorePanel]);

  useEffect(() => {
    setOptimisticMessages((current) =>
      current.filter(
        (optimisticMessage) =>
          !messages.some(
            (message) =>
              message.senderRole === optimisticMessage.senderRole &&
              message.text === optimisticMessage.text &&
              Math.abs(
                getChatTimestampMs(message.timestamp) -
                  getChatTimestampMs(optimisticMessage.timestamp),
              ) <
                2 * 60 * 1000,
          ),
      ),
    );
  }, [messages]);

  const displayTimeline = useMemo((): ChatTimelineItem[] => {
    const messageItems = [
      ...messages.map((message) => ({
        kind: 'message' as const,
        id: message.id,
        timestamp: message.timestamp,
        sortMs: getChatTimestampMs(message.timestamp),
        message,
      })),
      ...optimisticMessages.map((message) => ({
        kind: 'message' as const,
        id: message.id,
        timestamp: message.timestamp,
        sortMs: getChatTimestampMs(message.timestamp),
        message,
      })),
    ];
    const items: ChatTimelineItem[] = [
      ...messageItems,
      ...chatEvents.map((event) => ({
        kind: 'event' as const,
        id: event.id,
        timestamp: event.timestamp,
        sortMs: resolveParticipantAddedEventSortMs(
          event,
          messageItems.map((item) => item.message),
        ),
        event,
      })),
    ];
    return items.sort(compareChatTimelineItems);
  }, [messages, chatEvents, optimisticMessages]);

  const displayMessages = useMemo(
    () =>
      displayTimeline
        .filter(
          (item): item is Extract<ChatTimelineItem, { kind: 'message' }> => item.kind === 'message',
        )
        .map((item) => item.message),
    [displayTimeline],
  );
  const hasInvestorMessage = useMemo(
    () => displayMessages.some((message) => message.senderRole === 'investor'),
    [displayMessages],
  );
  const hasTeamReply = useMemo(
    () => displayMessages.some((message) => message.senderRole === 'admin' && message.text.trim()),
    [displayMessages],
  );
  const lastInvestorMessage = useMemo(
    () =>
      [...displayMessages].reverse().find((message) => message.senderRole === 'investor') ?? null,
    [displayMessages],
  );
  const conversationEnded =
    isTranscriptMode ||
    liveConversationStatus === 'resolved' ||
    selectedConversation?.status === 'resolved' ||
    transcriptData?.status === 'resolved';

  const chatAiAssist = useChatAiAssist({
    conversationId: chat?.openConversationId ?? null,
    lastInvestorMessage,
    disabled: !chat?.openConversationId || isSending || conversationEnded,
  });

  useEffect(() => {
    if (!chat?.openConversationId || !chat.isPanelOpen || isTranscriptMode) {
      return;
    }

    void FidesChatAdminService.markRead(chat.openConversationId)
      .then(() => {
        window.dispatchEvent(new Event(FIDES_CHAT_REFRESH_EVENT));
      })
      .catch((markReadError) => {
        console.error('Failed to mark conversation read', markReadError);
        setToastMessage({ type: 'error', text: 'Unable to mark chat as read.' });
      });
  }, [chat?.isPanelOpen, chat?.openConversationId, isTranscriptMode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages.length, loading]);

  useEffect(() => {
    if (error) {
      setToastMessage({ type: 'error', text: error });
    }
  }, [error]);

  const contactName =
    selectedConversation?.contact?.name?.trim() ||
    transcriptData?.contactName?.trim() ||
    selectedConversation?.contact?.email?.trim() ||
    'Investor chat';
  const contactEmail = selectedConversation?.contact?.email?.trim() || '';
  const contactExternalId =
    selectedConversation?.contact?.externalId ?? transcriptData?.contactExternalId ?? '';
  const convId = selectedConversation?.conversationId ?? chat?.openConversationId ?? '';
  const contextData = (selectedConversation?.context ??
    transcriptData?.context ??
    null) as ChatContext | null;
  const contextLabel = buildContextLabel(contextData);
  const contextLink = buildContextDeepLink(contextData, contactExternalId, convId);
  const effectiveOwnerName =
    selectedConversation?.ownerAdminName?.trim() || team.effectiveOwnerName || null;

  const investorExternalIdFromChat = useMemo(() => {
    const withContext = [...displayMessages]
      .reverse()
      .find((message) => message.context?.investorExternalId);
    return withContext?.context?.investorExternalId ?? null;
  }, [displayMessages]);

  const resolveEntityOptions = useMemo((): ChatResolveEntityOption[] => {
    const options: ChatResolveEntityOption[] = [];
    const effectiveContactId = contactExternalId || transcriptData?.contactExternalId || '';
    const effectiveContactName =
      contactName !== 'Investor chat'
        ? contactName
        : transcriptData?.contactName?.trim() || contactName;

    if (effectiveContactId) {
      options.push({
        modelType: 'CONTACT',
        modelExternalId: effectiveContactId,
        label: `Contact: ${effectiveContactName}`,
      });
    }

    if (investorExternalIdFromChat) {
      const fundLabel = contextData?.fundOpportunityExternalId
        ? contextLabel?.split(' – ')[0] || 'Fund investment'
        : 'Fund investment';
      options.push({
        modelType: 'INVESTOR',
        modelExternalId: investorExternalIdFromChat,
        label: `Investment: ${fundLabel}`,
      });
    }

    return options;
  }, [
    contactExternalId,
    contactName,
    contextData,
    contextLabel,
    investorExternalIdFromChat,
    transcriptData?.contactExternalId,
    transcriptData?.contactName,
  ]);

  const showToast = useCallback((type: 'error' | 'success', text: string) => {
    setToastMessage({ type, text });
  }, []);

  const handleSend = useCallback(async () => {
    const trimmedText = inputValue.trim();

    if (!chat?.openConversationId || !trimmedText || isSending || conversationEnded) {
      return;
    }

    const optimisticMessage: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      senderId: user?.sub ?? 'admin',
      senderName: user?.name ?? user?.email ?? 'Admin',
      senderRole: 'admin',
      text: trimmedText,
      timestamp: new Date(),
    };

    setOptimisticMessages((current) => [...current, optimisticMessage]);
    setInputValue('');
    setIsSending(true);
    const wasUnowned = team.wasUnownedAtSend();

    try {
      await FidesChatAdminService.sendMessage(chat.openConversationId, trimmedText);
      if (wasUnowned) {
        team.applyOwnershipAfterSend();
      }
      await team.refreshAfterSend();
    } catch (sendError) {
      console.error('Failed to send chat message', sendError);
      setOptimisticMessages((current) =>
        current.filter((message) => message.id !== optimisticMessage.id),
      );
      setInputValue(trimmedText);
      setToastMessage({ type: 'error', text: 'Unable to send message.' });
    } finally {
      setIsSending(false);
    }
  }, [inputValue, chat?.openConversationId, isSending, conversationEnded, user, team]);

  const openDraftNotes = useCallback(() => {
    if (!chat?.openConversationId || !hasTeamReply) {
      return;
    }
    setResolveDraftsOpen(true);
  }, [chat?.openConversationId, hasTeamReply]);

  return {
    chat,
    isTranscriptMode,
    loading,
    displayTimeline,
    chatLinkCorpus,
    chatAiAssist,
    inputValue,
    setInputValue,
    isSending,
    resolveDraftsOpen,
    setResolveDraftsOpen,
    liveResolvedBy,
    toastMessage,
    setToastMessage,
    messagesEndRef,
    contactName,
    contactEmail,
    contactExternalId,
    convId,
    contextData,
    contextLabel,
    contextLink,
    effectiveOwnerName,
    hasInvestorMessage,
    hasTeamReply,
    conversationEnded,
    resolveEntityOptions,
    handleSend,
    openDraftNotes,
    team,
    showToast,
  };
}

export default useFidesChatPanel;
